import { Wrench, Car, MapPin, FileText } from "lucide-react";
import { formatDateShort } from "@/utils/dateFormat";
import { useState, useEffect, useRef } from "react";
import { useMaintenanceRecord } from "@/hooks/useVehicleMaintenance";
import { useSession } from "@/hooks/useSessions";
import { VehicleMaintenanceModal } from "./VehicleMaintenanceModal";
import { useTranslation } from "react-i18next";
import SessionModal from "../sessions/SessionModal";
import type {
  VehicleTimelineEvent,
  VehicleTimelineEventType,
  Vehicle,
  VehicleMaintenanceTimelineDetails,
  VehicleSessionTimelineDetails,
} from "@/types";

interface VehicleTimelineProps {
  events: VehicleTimelineEvent[];
  vehicle?: Vehicle; // Vehicle data to get odometer unit
  isLoading?: boolean;
 className?: string;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isFetchingMore?: boolean;
}

const getEventIcon = (type: VehicleTimelineEventType) => {
  if (type === "maintenance") {
 return <Wrench className="h-3 w-3" />;
  }
  if (type === "session") {
 return <Car className="h-3 w-3" />;
  }
 return <FileText className="h-3 w-3" />;
};

const getEventColor = (type: VehicleTimelineEventType) => {
  switch (type) {
    case "maintenance":
      return "bg-orange-500 border-orange-200";
    case "session":
      return "bg-primary-500 border-primary-200";
    default:
      return "bg-secondary-500 border-secondary-200";
  }
};

const formatTime = (dateString: string) => {
  return formatDateShort(dateString);
};

export function VehicleTimeline({
  events,
  vehicle,
  isLoading,
  className = "",
  onLoadMore,
  hasMore = false,
  isFetchingMore = false,
}: VehicleTimelineProps) {
  const { t } = useTranslation(["pages", "common"]);
  const [selectedEvent, setSelectedEvent] =
    useState<VehicleTimelineEvent | null>(null);

  // Extract IDs for hooks based on selected event
  const maintenanceId =
    selectedEvent?.type === "maintenance"
      ? selectedEvent.id.replace("maintenance-", "")
      : "";
  const sessionId =
    selectedEvent?.type === "session"
      ? selectedEvent.id.replace("session-", "")
      : "";

  // NOTE: We intentionally do NOT call data-fetching hooks here because
  // this component is often rendered in unit tests without a QueryClientProvider.
  // Instead, we use lightweight wrapper components that fetch the data only when
  // the modal needs to be displayed, ensuring hooks are used safely within a
  // QueryClientProvider at runtime while keeping tests unaffected.

  // IntersectionObserver ref (must be declared before early returns)
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Observer effect for infinite scroll
  useEffect(() => {
    if (!hasMore || !onLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          onLoadMore();
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0,
      },
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, onLoadMore]);

  const MaintenanceModalLoader = ({
    id,
    onClose,
  }: {
    id: string;
    onClose: () => void;
  }) => {
    const { data: maintenanceRecord, isLoading } = useMaintenanceRecord(id);

    if (isLoading || !vehicle) return null;

    return (
      <VehicleMaintenanceModal
        isOpen={true}
        onClose={onClose}
        vehicle={vehicle}
        maintenance={maintenanceRecord}
      />
    );
  };

  const SessionModalLoader = ({
    id,
    onClose,
  }: {
    id: string;
    onClose: () => void;
  }) => {
    const { data: sessionRecord, isLoading } = useSession(id);

    if (isLoading) return null;

    return (
      <SessionModal isOpen={true} onClose={onClose} session={sessionRecord} />
    );
  };

  if (isLoading) {
    return (
 <div className={`space-y-2 ${className}`}>
        {Array.from({ length: 4 }).map((_, i) => (
 <div key={i} className="flex items-center space-x-3">
 <div className="animate-pulse bg-secondary-300 rounded-full h-6 w-6"></div>
 <div className="flex-1 space-y-1">
 <div className="animate-pulse bg-secondary-300 rounded h-3 w-3/4"></div>
 <div className="animate-pulse bg-secondary-300 rounded h-2 w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className={`text-center py-8 text-secondary-500 ${className}`}>
        <FileText className="h-12 w-12 mx-auto mb-4 text-secondary-400" />
        <p>{t("pages:vehicles.timeline.emptyTitle")}</p>
        <p className="text-sm mt-2">
          {t("pages:vehicles.timeline.emptyBody")}
        </p>
      </div>
    );
  }

  // Sort events by date (newest first)
  const sortedEvents = [...events].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return (
 <div className={`space-y-2 ${className}`}>
 <div className="relative">
        {/* Timeline line */}
 <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-secondary-200"></div>

        {sortedEvents.map((event) => (
          <div
            key={event.id}
 className="relative flex items-start space-x-3 pb-2 cursor-pointer"
            onClick={() => setSelectedEvent(event)}
          >
            {/* Timeline dot */}
            <div
 className={`
              relative z-10 flex items-center justify-center w-6 h-6 rounded-full
              text-white ${getEventColor(event.type)} flex-shrink-0
            `}
            >
              {getEventIcon(event.type)}
            </div>

            {/* Event content */}
 <div className="flex-1 min-w-0">
 <div className="bg-white rounded border border-secondary-200 p-2">
                {/* Compact header with title, date, and location in one line */}
 <div className="flex items-center justify-between mb-1">
 <h3 className="text-sm font-medium text-secondary-900 truncate">
                    {event.title}
                  </h3>
 <time className="text-xs text-secondary-500 ml-2 flex-shrink-0">
                    {formatTime(event.date)}
                  </time>
                </div>

                {/* Single line with key info */}
 <div className="flex items-center justify-between text-xs text-secondary-600">
 <div className="flex items-center space-x-3">
                    {/* Location */}
                    {event.location && (
 <div className="flex items-center">
 <MapPin className="h-3 w-3 mr-1 text-secondary-400" />
 <span className="truncate">{event.location}</span>
                      </div>
                    )}

                    {/* Type badge */}
                    <span
                      className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        event.type === "maintenance"
                          ? "bg-orange-100 text-orange-800"
                          : "bg-primary-100 text-primary-800"
                      }`}
                    >
                      {event.type === "maintenance"
                        ? (event.details as VehicleMaintenanceTimelineDetails)
                            .workType ||
                          (event.details as VehicleMaintenanceTimelineDetails)
                            .maintenanceType
                        : t("pages:vehicles.timeline.sessionBadge")}
                    </span>
                  </div>

                  {/* Key metric */}
 <div className="flex items-center space-x-2 flex-shrink-0">
                    {event.details.type === "maintenance" &&
                      (event.details as VehicleMaintenanceTimelineDetails)
                        .mileage && (
 <span className="text-secondary-500">
                          {(
                            event.details as VehicleMaintenanceTimelineDetails
                          ).mileage?.toLocaleString()}{" "}
                          {vehicle?.odometerUnit || "km"}
                        </span>
                      )}

                    {event.details.type === "session" &&
                      (event.details as VehicleSessionTimelineDetails)
                        .lapCount && (
                        <span className="text-secondary-500">
                          {t("pages:vehicles.timeline.lapsCount", {
                            count: (event.details as VehicleSessionTimelineDetails)
                              .lapCount,
                          })}
                        </span>
                      )}

                    {event.details.type === "session" &&
                      (event.details as VehicleSessionTimelineDetails)
                        .bestLapTime && (
 <span className="text-secondary-500">
                          {(
                            ((event.details as VehicleSessionTimelineDetails)
                              .bestLapTime || 0) / 1000
                          ).toFixed(1)}
                          s
                        </span>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Sentinel for infinite scroll */}
        {hasMore && <div ref={loadMoreRef} className="h-1 w-full" />}
        {isFetchingMore && (
          <div className="text-center py-2 text-sm text-secondary-500">
            {t("pages:vehicles.timeline.loadingMore")}
          </div>
        )}
        {/* Manual fallback */}
        {onLoadMore && !isFetchingMore && (
          <div className="text-center py-2">
            <button
              type="button"
              onClick={onLoadMore}
              className="text-primary-600 hover:underline text-sm"
              disabled={!hasMore}
            >
              {hasMore ? t("pages:vehicles.timeline.loadMore") : t("pages:vehicles.timeline.noMore")}
            </button>
          </div>
        )}
      </div>

      {/* Maintenance Modal (lazy loader) */}
      {selectedEvent?.type === "maintenance" && maintenanceId && (
        <MaintenanceModalLoader
          id={maintenanceId}
          onClose={() => setSelectedEvent(null)}
        />
      )}

      {/* Session Modal (lazy loader) */}
      {selectedEvent?.type === "session" && sessionId && (
        <SessionModalLoader
          id={sessionId}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </div>
  );
}
